import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import { Checkbox } from "@/components/core/forms/checkbox";
import { CheckboxGroup } from "@/components/core/forms/checkbox-group";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormIcon, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import FormCalendar from "@/components/core/forms/form-calendar";
import FormNumber from "@/components/core/forms/form-number";
import { Input } from "@/components/core/forms/input";
import { InputFile } from "@/components/core/forms/input-file";
import { RadioGroup, RadioGroupItem } from "@/components/core/forms/radio-group";
import Select from "@/components/core/forms/select";
import { IconAlertCircle, IconLock, IconUser } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
export const Route = createFileRoute("/register")({
	component: RouteComponent,
});

function RouteComponent() {
	const form = useForm({
		defaultValues: {
			id: null,
			username: null,
			email: null,
			contact_number: null,
			first_name: null,
			middle_name: null,
			last_name: null,
			sex: null,
			suffix: null,
			birthdate: null,
			birthplace: null,
			civil_status: null,
			citizenship: null,
			religion: null,
			preferred_name: null,
			preferred_middle_initials: null,
			roll_number: null,
			bar_type: null,
			bar_year: null,
			is_bar_member: false,
		},
	});

	const [isBarMember, setIsBarMember] = useState(form.getValues().is_bar_member);

	const handleBarMemberChange = (value: any) => {
		setIsBarMember(value === "rb1");
	};

	return (
		<div className="flex justify-center items-center min-h-screen bg-background">
			<div className="flex flex-col p-8 gap-5 bg-background max-w-6xl w-full">
				<Form {...form}>
					<div align="center">
						<div className="z-[2] flex flex-col justify-center items-center w-full gap-2">
							<h1 className="text-3xl font-bold text-center text-gray-800">New User Registration</h1>
						</div>
					</div>

					<Alert type="info" allowClose={true} title="Important:" icon={<IconAlertCircle size={20} stroke={2} />}>
						Please read the following before proceeding:
						<br />
						<br />
						All fields marked with an asterisk (*) are required.
						<br />
						<br />
						Please input your name as it appears on your Birth Certificate or Marriage Certificate, whichever is applicable. You may search online for special
						characters (Ñ, ñ, etc.) and then copy-paste it on this form.
						<br />
					</Alert>

					<div className="flex flex-row gap-3">
						<FormField
							control={form.control}
							name="is_bar_member"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Are you a member of the bar?</FormLabel>
									</div>
									<RadioGroup
										onValueChange={(value) => {
											field.onChange(value);
											handleBarMemberChange(value);
										}}
										value={field.value}
									>
										<FormItem>
											<div className="flex flex-row items-center w-full gap-4">
												<FormControl>
													<RadioGroupItem value="rb1" />
												</FormControl>
												<FormLabel>
													<div className="flex flex-col w-full">
														<span className="text-base text-foreground">Yes</span>
													</div>
												</FormLabel>

												<FormControl>
													<RadioGroupItem value="rb2" />
												</FormControl>
												<FormLabel>
													<div className="flex flex-col w-full">
														<span className="text-base text-foreground">No</span>
													</div>
												</FormLabel>
											</div>
										</FormItem>
									</RadioGroup>
								</FormItem>
							)}
						/>
					</div>

					<div className="flex flex-row gap-3">
						<FormField
							control={form.control}
							name="last_name"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Last Name</FormLabel>
										<FormDescription />
									</div>
									<FormGroup iconPosition="left">
										<Input autoComplete="off" placeholder="Last Name" {...field} />
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="first_name"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>First Name</FormLabel>
										<FormDescription />
									</div>
									<FormGroup iconPosition="left">
										<Input autoComplete="off" placeholder="First Name" {...field} />
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="middle_name"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel>Middle Name</FormLabel>
										<FormDescription />
									</div>
									<FormGroup iconPosition="left">
										<Input autoComplete="off" placeholder="Middle Name" {...field} />
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="preferred_middle_initial"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel>Middle Initial</FormLabel>
										<FormDescription />
									</div>
									<FormGroup iconPosition="left">
										<Input autoComplete="off" placeholder="M.I." {...field} />
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="suffix"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel>Suffix</FormLabel>
										<FormDescription />
									</div>
									<FormGroup>
										<Select
											placeholder="Select Suffix"
											onValueChange={field.onChange}
											defaultValue={field.value}
											options={[
												{
													value: "JR.",
													label: "JR.",
												},
												{
													value: "SR.",
													label: "SR.",
												},
												{
													value: "I",
													label: "I",
												},
												{
													value: "II",
													label: "II",
												},
												{
													value: "III",
													label: "III",
												},
												{
													value: "IV",
													label: "IV",
												},
												{
													value: "V",
													label: "V",
												},
												{
													value: "VI",
													label: "VI",
												},
												{
													value: "VII",
													label: "VII",
												},
												{
													value: "VIII",
													label: "VIII",
												},
												{
													value: "IX",
													label: "IX",
												},
												{
													value: "X",
													label: "X",
												},
												{
													value: "XI",
													label: "XI",
												},
												{
													value: "XII",
													label: "XII",
												},
												{
													value: "XIII",
													label: "XIII",
												},
												{
													value: "XIX",
													label: "XIX",
												},
												{
													value: "XX",
													label: "XX",
												},
											]}
										/>
									</FormGroup>
								</FormItem>
							)}
						/>
					</div>

					<div className="flex flex-row gap-3">
						<FormField
							control={form.control}
							name="birthdate"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Birthdate</FormLabel>
									</div>
									<FormGroup>
										<FormCalendar onChange={(date) => console.log(date)} id="calendar" showIcon={true} />
									</FormGroup>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="age"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel>Age</FormLabel>
										<FormDescription />
									</div>
									<FormGroup iconPosition="left">
										<Input autoComplete="off" disabled placeholder="Age" {...field} />
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="sex"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Sex Assigned at Birth</FormLabel>
									</div>
									<RadioGroup onValueChange={field.onChange} defaultValue={field.value}>
										<FormItem>
											<div className="flex flex-row items-center w-full gap-4">
												<FormControl>
													<RadioGroupItem value="rb1" />
												</FormControl>
												<FormLabel>
													<div className="flex flex-col w-full">
														<span className="text-base text-foreground">Male</span>
													</div>
												</FormLabel>

												<FormControl>
													<RadioGroupItem value="rb2" />
												</FormControl>
												<FormLabel>
													<div className="flex flex-col w-full">
														<span className="text-base text-foreground">Female</span>
													</div>
												</FormLabel>
											</div>
										</FormItem>
									</RadioGroup>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="preferred_gender"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel>Preferred Gender Pronouns, if any</FormLabel>
										<FormDescription />
									</div>
									<FormGroup>
										<Select
											placeholder="Select Preferred Gender"
											onValueChange={field.onChange}
											defaultValue={field.value}
											options={[
												{
													value: "He/Him",
													label: "He/Him",
												},
												{
													value: "She/Her",
													label: "She/Her",
												},
												{
													value: "They/Them",
													label: "They/Them",
												},
											]}
										/>
									</FormGroup>
								</FormItem>
							)}
						/>
					</div>

					<div className="flex flex-row items-center w-full gap-2">
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Email Address</FormLabel>
									</div>
									<FormGroup iconPosition="left">
										<Input autoComplete="off" placeholder="Email Address" {...field} />
									</FormGroup>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="secondary_email"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel>Secondary Email Address</FormLabel>
									</div>
									<FormGroup iconPosition="left">
										<Input autoComplete="off" placeholder="Secondary Email Address" {...field} />
									</FormGroup>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="contact_number"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Contact Number</FormLabel>
									</div>
									<FormGroup iconPosition="left">
										<Input autoComplete="off" placeholder="Contact Number" {...field} />
									</FormGroup>
								</FormItem>
							)}
						/>
					</div>

					{isBarMember && (
						<Alert type="info" allowClose={true} title="Please complete the additional fields:" icon={<IconAlertCircle size={20} stroke={2} />} />
					)}

					{isBarMember && (
						<div className="flex flex-row items-center w-full gap-2">
							<FormField
								control={form.control}
								name="preferred_gender"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Bar Type</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Select
												placeholder="Select Bar Type"
												onValueChange={field.onChange}
												defaultValue={field.value}
												options={[
													{
														value: "Philippine Bar",
														label: "Philippine Bar",
													},
													{
														value: "Pre-war Philippine Bar",
														label: "Pre-war Philippine Bar",
													},
													{
														value: "Shari'ah",
														label: "Shari'ah",
													},
												]}
											/>
										</FormGroup>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="bar_year"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Bar Year</FormLabel>
										</div>
										<FormGroup iconPosition="left">
											<Input autoComplete="off" placeholder="Bar Year" {...field} />
										</FormGroup>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="roll_number"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Roll Number</FormLabel>
										</div>
										<FormGroup iconPosition="left">
											<Input autoComplete="off" placeholder="Roll Number" {...field} />
										</FormGroup>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="date_signed"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Date Signed</FormLabel>
										</div>
										<FormGroup>
											<FormCalendar onChange={(date) => console.log(date)} id="calendar" showIcon={true} />
										</FormGroup>
									</FormItem>
								)}
							/>
						</div>
					)}

					{isBarMember && (
						<div className="flex flex-row items-center w-full gap-2">
							<FormField
								control={form.control}
								name="latest_unretouched_photo"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel>Latest Unretouched Photo</FormLabel>
										</div>
										<FormGroup>
											<InputFile {...field} />
										</FormGroup>
									</FormItem>
								)}
							/>
						</div>
					)}

					{isBarMember && (
						<div className="flex flex-row items-center w-full gap-2">
							<FormField
								control={form.control}
								name="government_id_front"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel>Government ID (Front)</FormLabel>
										</div>
										<FormGroup>
											<InputFile {...field} />
										</FormGroup>
									</FormItem>
								)}
							/>
						</div>
					)}

					{isBarMember && (
						<div className="flex flex-row items-center w-full gap-2">
							<FormField
								control={form.control}
								name="government_id_back"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel>Government ID (Back)</FormLabel>
										</div>
										<FormGroup>
											<InputFile {...field} />
										</FormGroup>
									</FormItem>
								)}
							/>
						</div>
					)}

					{isBarMember && (
						<div className="flex flex-row items-center w-full gap-2">
							<FormField
								control={form.control}
								name="company_id"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel>Company ID</FormLabel>
										</div>
										<FormGroup>
											<InputFile {...field} />
										</FormGroup>
									</FormItem>
								)}
							/>
						</div>
					)}

					{!isBarMember && (
						<div className="flex flex-row items-center w-full gap-2">
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Password</FormLabel>
										</div>
										<FormGroup iconPosition="left">
											<Input type="password" placeholder="Create a Password" {...field} />
											<FormIcon>
												<IconLock size={18} stroke={1.5} />
											</FormIcon>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="confirm_password"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Re-type Password</FormLabel>
										</div>
										<FormGroup iconPosition="left">
											<Input type="password" placeholder="Re-type Password" {...field} />
											<FormIcon>
												<IconLock size={18} stroke={1.5} />
											</FormIcon>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					)}

					{!isBarMember && (
						<div className="flex flex-row items-center w-full gap-2">
							<FormField
								control={form.control}
								name="security_question"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Security Question</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Select
												placeholder="Select Question"
												onValueChange={field.onChange}
												defaultValue={field.value}
												options={[
													{
														value: "What was the last name of your first boss?",
														label: "What was the last name of your first boss?",
													},
													{
														value: "What are the last four digits of your phone number?",
														label: "What are the last four digits of your phone number?",
													},
													{
														value: "Who is your favorite author?",
														label: "Who is your favorite author?",
													},
													{
														value: "What is the name of your favorite book?",
														label: "What is the name of your favorite book?",
													},
													{
														value: "What was the last name of your favorite teacher?",
														label: "What was the last name of your favorite teacher?",
													},
													{
														value: "Where did you spend your childhood summers?",
														label: "Where did you spend your childhood summers?",
													},
													{
														value: "What is the name of the hospital where you were born?",
														label: "What is the name of the hospital where you were born?",
													},
													{
														value: "What was your favorite food as a child?",
														label: "What was your favorite food as a child?",
													},
													{
														value: "in which town was your father born?",
														label: "in which town was your father born?",
													},
													{
														value: "in which town was your mother born?",
														label: "in which town was your mother born?",
													},
												]}
											/>
										</FormGroup>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="answer"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Answer</FormLabel>
										</div>
										<FormGroup iconPosition="left">
											<Input type="password" placeholder="Answer" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					)}

					<span className="text-base text-foreground">
						By creating an account, I agree on the collection and processing of my personal information as indicated in the Philippine Judiciary Platform.
					</span>

					<FormField
						control={form.control}
						name="agree"
						render={({ field }) => (
							<FormItem>
								<FormGroup>
									<div className="flex flex-row w-full gap-2">
										<FormControl>
											<Checkbox onCheckedChange={field.onChange} />
										</FormControl>
										<FormLabel>
											<div className="flex flex-col w-full">
												<span className="text-base text-foreground">I have read and agreed to the Data Privacy Notice.</span>
											</div>
										</FormLabel>
									</div>
								</FormGroup>
								<FormMessage />
							</FormItem>
						)}
					/>
					<div className="col-span-12 flex flex-col w-full">
						<Button className="w-full">Request Account</Button>
					</div>
				</Form>
			</div>
		</div>
	);
}
